/**
 * Workplan Template Selector
 * Beautiful grid view of available workplan service line templates
 * Based on ReportTemplateSelector pattern
 */

import React, { useState, useMemo } from 'react';
import { Card, Button, Badge, Input } from '@tracekit/core';
import { 
  FileText, Search, ListChecks, Building2, Droplet, 
  Leaf, Wrench, Microscope, AlertCircle 
} from 'lucide-react';
import { EmptyState, LoadingSpinner } from '@tracekit/core';

export interface WorkplanTemplate {
  key: string;
  serviceLine: string;
  template: {
    service_line?: string;
    additional_sections?: Array<{ name: string; description?: string }>;
    additional_questions?: Array<{ section: string; question: string }>;
    description?: string;
  };
}

interface WorkplanTemplateSelectorProps {
  templates: WorkplanTemplate[];
  onSelectTemplate: (template: WorkplanTemplate) => void;
  onPreviewTemplate?: (template: WorkplanTemplate) => void;
  isLoading?: boolean;
  title?: string;
  description?: string;
}

// Service line icons mapping
const SERVICE_LINE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'environmental consulting': Building2,
  'environmental assessment': Microscope,
  'contaminated sites': Droplet,
  'remediation': Droplet,
  'environmental engineering': Wrench,
  'permitting': FileText,
  'biology': Leaf,
  'environmental impact assessment': Leaf,
  'monitoring': AlertCircle,
  'sampling': Microscope,
  'technical services': Wrench,
  'data management': FileText,
};

// Service line colors
const SERVICE_LINE_COLORS: Record<string, string> = {
  'environmental consulting': 'from-blue-500 to-blue-600',
  'environmental assessment': 'from-green-500 to-green-600',
  'contaminated sites': 'from-orange-500 to-orange-600',
  'remediation': 'from-red-500 to-red-600',
  'environmental engineering': 'from-purple-500 to-purple-600',
  'permitting': 'from-indigo-500 to-indigo-600',
  'biology': 'from-emerald-500 to-emerald-600',
  'environmental impact assessment': 'from-teal-500 to-teal-600',
  'monitoring': 'from-cyan-500 to-cyan-600',
  'sampling': 'from-sky-500 to-sky-600',
  'technical services': 'from-violet-500 to-violet-600',
  'data management': 'from-slate-500 to-slate-600',
};

/**
 * Normalize service line name for comparison
 */
function normalizeServiceLine(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Get icon for service line
 */
function getServiceLineIcon(serviceLine: string): React.ComponentType<{ className?: string }> {
  const normalized = normalizeServiceLine(serviceLine);
  for (const [key, icon] of Object.entries(SERVICE_LINE_ICONS)) {
    if (normalized.includes(key)) {
      return icon;
    }
  }
  return ListChecks;
}

/**
 * Get color for service line
 */
function getServiceLineColor(serviceLine: string): string {
  const normalized = normalizeServiceLine(serviceLine);
  for (const [key, color] of Object.entries(SERVICE_LINE_COLORS)) {
    if (normalized.includes(key)) {
      return color;
    }
  }
  return 'from-gray-500 to-gray-600';
}

/**
 * Convert underscore template key to nice display name
 * Examples:
 * - contaminated_sites___remediation -> "Contaminated Sites & Remediation"
 * - environmental_consulting___assessment -> "Environmental Consulting & Assessment"
 * - biology___environmental_impact_assessment -> "Biology & Environmental Impact Assessment"
 */
function formatTemplateDisplayName(key: string): string {
  // Handle triple underscores (___) as " & "
  // Handle double underscores (__) as " & "
  // Handle single underscores (_) as spaces
  let formatted = key
    .replace(/___+/g, ' & ')
    .replace(/__+/g, ' & ')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(word => word.length > 0) // Remove empty strings
    .map(word => {
      // Capitalize first letter, lowercase rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
  
  // Clean up any double spaces
  formatted = formatted.replace(/\s+/g, ' ');
  
  return formatted;
}

/**
 * Filter and format templates for display.
 * 
 * This function:
 * 1. Formats display names for templates with underscore keys
 * 2. Removes duplicates based on normalized service line names
 * 3. Sorts templates for consistent display
 * 
 * Note: Quality filtering (removing temporary templates) should be done
 * via the backend API using the /api/past-projects/manage-templates endpoint.
 * The frontend just displays what's in the policy pack.
 */
function auditAndFilterTemplates(templates: WorkplanTemplate[]): WorkplanTemplate[] {
  const formattedTemplates: WorkplanTemplate[] = [];
  
  for (const template of templates) {
    // Format display name if it has underscore key
    let displayName = template.serviceLine;
    if (template.key.includes('_') && template.key === template.serviceLine) {
      // Only format if serviceLine hasn't been formatted yet (matches key)
      displayName = formatTemplateDisplayName(template.key);
    }
    
    formattedTemplates.push({
      ...template,
      serviceLine: displayName,
    });
  }
  
  // Remove duplicates by normalizing service line names
  const seen = new Set<string>();
  const deduplicated: WorkplanTemplate[] = [];
  
  // Sort templates by key for consistent ordering
  const sorted = [...formattedTemplates].sort((a, b) => a.key.localeCompare(b.key));

  for (const template of sorted) {
    const normalized = normalizeServiceLine(template.serviceLine);
    if (!seen.has(normalized)) {
      seen.add(normalized);
      deduplicated.push(template);
    }
  }

  return deduplicated;
}

export function WorkplanTemplateSelector({
  templates,
  onSelectTemplate,
  onPreviewTemplate,
  isLoading = false,
  title = 'Select Service Line Template',
  description = 'Choose a pre-configured workplan template for your service line',
}: WorkplanTemplateSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Audit and filter templates
  const filteredTemplates = useMemo(() => {
    const audited = auditAndFilterTemplates(templates);
    
    // Apply search filter
    if (!searchQuery) return audited;
    
    const query = searchQuery.toLowerCase();
    return audited.filter(({ serviceLine, template }) => {
      const serviceLineMatch = serviceLine.toLowerCase().includes(query);
      const descriptionMatch = (template.description || '').toLowerCase().includes(query);
      return serviceLineMatch || descriptionMatch;
    });
  }, [templates, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (filteredTemplates.length === 0) {
    return (
      <EmptyState
        icon={<ListChecks className="h-12 w-12" />}
        title={searchQuery ? "No Templates Match Your Search" : "No Templates Available"}
        description={searchQuery 
          ? "Try adjusting your search terms" 
          : "Policy pack does not contain workplan templates"}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          {title}
        </h2>
        <p className="text-gray-600 dark:text-gray-300">
          {description}
        </p>
      </div>

      {/* Search */}
      <div className="max-w-md relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
          className="w-full pl-10"
        />
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates.map((template) => {
          const sections = template.template.additional_sections || [];
          const questions = template.template.additional_questions || [];
          const Icon = getServiceLineIcon(template.serviceLine);
          const color = getServiceLineColor(template.serviceLine);

          return (
            <Card
              key={template.key}
              className="hover:shadow-lg transition-all duration-200 cursor-pointer group border-theme-border hover:border-theme-primary"
              onClick={() => onSelectTemplate(template)}
            >
              <div className="p-6 space-y-4">
                {/* Icon */}
                <div className={`w-12 h-12 rounded-lg bg-gradient-to-r ${color} flex items-center justify-center shadow-md`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>

                {/* Title and Description */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {template.serviceLine}
                  </h3>
                  {template.template.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                      {template.template.description}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="space-y-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-400">Sections:</span>
                    <Badge variant="outline" className="text-xs">
                      {sections.length}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-600 dark:text-gray-400">Questions:</span>
                    <Badge variant="outline" className="text-xs">
                      {questions.length}
                    </Badge>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    className="flex-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectTemplate(template);
                    }}
                  >
                    Use Template
                  </Button>
                  {onPreviewTemplate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreviewTemplate(template);
                      }}
                      title="Preview template"
                    >
                      <Search className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
